import { useState, useEffect, useCallback } from 'react'
import { userService } from '../services/userService'
import type { User, UserFilters } from '../types'

export function useUsers(initialFilters?: Partial<UserFilters>) {
  const [data, setData] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  })

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await userService.getAll(filters)
      // Filter only role === 'User' (vendedores), matching Angular behavior
      const filteredUsers = response.data.filter((u) => u.role === 'User')
      setData(filteredUsers)
      setTotal(response.total_items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar vendedores')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setPage = (page: number) => setFilters((prev) => ({ ...prev, page }))
  const setLimit = (limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))
  const setSearch = (param: string, value: string) =>
    setFilters((prev) => ({ ...prev, param, value, page: 1 }))
  const applyFilters = (partial: Partial<UserFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: 1 }))

  const clearSearch = () =>
    setFilters((prev) => {
      const next = { ...prev, page: 1 }
      delete next.param
      delete next.value
      return next
    })

  return {
    data,
    total,
    isLoading,
    error,
    filters,
    setPage,
    setLimit,
    setSearch,
    applyFilters,
    clearSearch,
    refetch: fetchData,
  }
}
